import React, { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  GroupProps,
  OptionProps,
  components,
} from 'react-select'
import {
  getBlogCategoryReplacement,
  getBlogPageReplacement,
  getBlogPostReplacement,
  isBlogPageUrlPlaceholder,
  isBlogPostUrlPlaceholder,
} from 'common/utils/blogReplacementsUtils'
import { blogSelectors, useBlog } from 'client/store'
import { IdName } from 'client/store/blog/blogReducer'

const optionStyles = (
  provided: CSSObjectWithLabel,
  state: OptionProps<OptionType, false, GroupBase<OptionType>>,
) => ({
  ...provided,
  padding: '4px 20px',
  fontSize: '14px',
  color: '#1e293b',
  cursor: 'pointer',
  backgroundColor: state.isSelected
    ? '#f1f5f9'
    : state.isFocused
      ? '#f8fafc'
      : 'white',
  '&:active': {
    backgroundColor: '#f1f5f9',
  },
})

const customStyles = {
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: 0,
    borderRadius: '8px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #E4E5E7',
    overflow: 'hidden',
  }),
  menuList: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: 0,
  }),
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    minHeight: '40px',
    borderRadius: '8px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#cbd5e1',
    },
  }),
  option: optionStyles,
  group: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: 0,
  }),
  groupHeading: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: '0 8px',
  }),
  valueContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: '0 12px',
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#1e293b',
    fontSize: '14px',
  }),
  input: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#1e293b',
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#94a3b8',
    fontSize: '14px',
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#94a3b8',
    '&:hover': {
      color: '#64748b',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
}

const formatGroupLabel = (data: GroupType) => (
  <div className="flex items-center justify-between w-full px-3 py-1 text-XS">
    <span>{data.label}</span>
    <span className="bg-bgSecondary rounded-full text-textTertiary text-XS text-center w-[18px] h-[18px] flex items-center justify-center">
      {data.options.length}
    </span>
  </div>
)

type GroupType = {
  label: string
  options: OptionType[]
  groupIndex: number
}

type OptionType = {
  value: string
  label: string
}

interface LinkSourceBlogProps {
  url?: string
  update: (url: string) => void
}

const Group = (props: GroupProps<OptionType, false, GroupType>) => {
  const isLastGroup = props.data.groupIndex === 2
  return (
    <div>
      <components.Group {...props} />
      {!isLastGroup && <div className="h-px bg-bgTertiary my-1 mx-3" />}
    </div>
  )
}

export const LinkSourceBlog = ({ url, update }: LinkSourceBlogProps) => {
  const { t } = useTranslation()
  const blogPages = useBlog(blogSelectors.getBlogPages)
  const blogPosts = useBlog(blogSelectors.getBlogPosts)
  const blogCategories = useBlog(blogSelectors.getBlogCategories)

  let valueOption
  if (url) {
    const urlPlaceholder = url.replace(/\d+/g, ' ')
    const findMatch = (
      items: IdName[],
      getReplacement: (pageId: number) => string,
    ) => items.find(item => getReplacement(item.id) === url)

    if (isBlogPageUrlPlaceholder(urlPlaceholder)) {
      const page = findMatch(blogPages, getBlogPageReplacement)
      if (page) {
        valueOption = {
          label: page.name,
          value: getBlogPageReplacement(page.id),
        }
      }
    } else if (isBlogPostUrlPlaceholder(urlPlaceholder)) {
      const postPage = findMatch(blogPosts, getBlogPostReplacement)
      if (postPage) {
        valueOption = {
          label: postPage.name,
          value: getBlogPostReplacement(postPage.id),
        }
      }
    } else {
      const category = findMatch(blogCategories, getBlogCategoryReplacement)
      if (category) {
        valueOption = {
          label: category.name,
          value: getBlogCategoryReplacement(category.id),
        }
      }
    }
  }

  const setBlogLink = (option: OptionType | null) => {
    if (option) {
      update(option.value)
    }
  }

  const groupedOptions = [
    {
      label: t('entity_settings.button.blog_link.pages_group_title'),
      options: blogPages.map(page => ({
        label: page.name,
        value: getBlogPageReplacement(page.id),
      })),
      groupIndex: 0,
    },
    {
      label: t('entity_settings.button.blog_link.categories_group_title'),
      options: blogCategories.map(category => ({
        label: category.name,
        value: getBlogCategoryReplacement(category.id),
      })),
      groupIndex: 1,
    },
    {
      label: t('entity_settings.button.blog_link.posts_group_title'),
      options: blogPosts.map(postPage => ({
        label: postPage.name,
        value: getBlogPostReplacement(postPage.id),
      })),
      groupIndex: 2,
    },
  ]

  return (
    <div className="flex justify-between items-center py-3 px-6">
      <label
        htmlFor="blog-link-input"
        className="block text-left text-L font-semibold "
      >
        {t('components.core.text_editor.link_toolbar.blog_link')}
      </label>
      <Select
        placeholder={t('entity_settings.button.blog_link.choose_blog_source')}
        styles={customStyles}
        options={groupedOptions}
        formatGroupLabel={formatGroupLabel}
        onChange={setBlogLink}
        value={valueOption}
        components={{
          Group: Group,
        }}
        className=" w-2/3 text-left"
      />
    </div>
  )
}
